<template>
  <div>
    <h3>Service Categories</h3>

    <div
      v-if="isLoading"
      class="text-center pt-3"
    >
      <b-spinner label="Loading..." />
    </div>
    <template v-else>
      <div class="custom-search d-flex justify-content-between">
        <b-form-group>
          <b-form-input
            v-model="searchTerm"
            placeholder="Search by name"
            type="text"
            class="d-inline-block"
          />
        </b-form-group>
        <b-form-group v-if="canCreateServiceCategories">
          <b-button
            variant="primary"
            class="mx-1 float-right"
            size="sm"
            @click="openServiceCategoryModal(null)"
          >
            <span>Add service category</span>
          </b-button>
        </b-form-group>
      </div>
      <vue-good-table
        :columns="displayColumns"
        :rows="filteredServiceCategories"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
      >
        <template #table-row="props">
          <span v-if="props.column.field === 'picture'">
            <img
              :src="pictureUrl(props.row.picture)"
              class="table-row-thumb"
              alt=""
            >
          </span>
          <span v-else-if="props.column.field === 'parent_category_id'">
            {{
              props.row.parent_category_id
                ? serviceCategories.find((c) => c.id === props.row.parent_category_id)?.name
                : ''
            }}
          </span>
          <span
            v-else-if="props.column.field === 'action' && canChangeServiceCategories"
            class="action-link-group service-categories-action"
          >
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              right
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item @click="openServiceCategoryModal(props.row)">
                <feather-icon
                  icon="Edit2Icon"
                  class="mr-50"
                />
                <span>Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="deleteServiceCategory(props.row)">
                <feather-icon
                  icon="TrashIcon"
                  class="mr-50"
                />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
          <DatatableCellValue
            v-else
            :field="props.column.field"
            :value="props.formattedRow[props.column.field]"
          />
        </template>

        <template #pagination-bottom="props">
          <TablePagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              @input="(value) => props.pageChanged({ currentPage: value })"
              @per-page-change="(n) => { pageLength = n; props.perPageChanged({ currentPerPage: n }) }"
            />
        </template>
      </vue-good-table>
    </template>
    <ManageServiceCategory
      :click-date="clickDate"
      :service-category="serviceCategory"
      :service-categories="serviceCategories"
    />
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'
import { VueGoodTable } from 'vue-good-table-next'
import {
  BButton,
  BDropdown,
  BDropdownItem,
  BFormGroup,
  BFormInput,
  BPagination,
  BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/Components/Elements/TablePagination.vue'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import ManageServiceCategory from '@/views/Components/ManageServiceCategory.vue'
import { canStaffChange, canStaffCreate } from '@/libs/staffPermissions'
import { resolvePictureUrl } from '@/libs/pictureUrl'
import { $themeConfig } from '@themeConfig'
import DatatableCellValue from '@/views/Components/Elements/DatatableCellValue.vue'

const NO_IMAGE = '/static_images/no_image.png'

export default {
  components: {
    TablePagination,
    ManageServiceCategory,
    DatatableCellValue,
    BButton,
    BDropdown,
    BDropdownItem,
    BFormGroup,
    BFormInput,
    BSpinner,
    BPagination,
    VueGoodTable,
  },
  data() {
    const { noUnitImage } = $themeConfig.app

    return {
      noUnitImage,
      serviceCategory: null,
      clickDate: null,
      isLoading: true,
      searchTerm: '',
      columns: [
        {
          label: 'Picture',
          field: 'picture',
          thClass: 'table-picture-col',
          tdClass: 'table-picture-col',
          sortable: false,
        },
        {
          label: 'Name',
          field: 'name',
        },
        {
          label: 'Description',
          field: 'description',
        },
        {
          label: 'Parent Category',
          field: 'parent_category_id',
        },
        {
          label: 'Actions',
          field: 'action',
          thClass: 'text-center service-categories-action-col',
          tdClass: 'text-center service-categories-action-col',
          sortable: false,
        },
      ],
      pageLength: 25,
    }
  },
  computed: {
    ...mapGetters({
      serviceCategories: 'serviceCategories/serviceCategories',
      authUser: 'auth/user',
    }),
    canCreateServiceCategories() {
      return canStaffCreate(this.authUser, 'service_categories')
    },
    canChangeServiceCategories() {
      return canStaffChange(this.authUser, 'service_categories')
    },
    displayColumns() {
      if (this.canChangeServiceCategories) {
        return this.columns
      }
      return this.columns.filter((column) => column.field !== 'action')
    },
    filteredServiceCategories() {
      const keyword = String(this.searchTerm || '').trim().toLowerCase()
      if (!keyword) {
        return this.serviceCategories
      }
      return (this.serviceCategories || []).filter((category) => (
        String(category.name || '').toLowerCase().includes(keyword)
      ))
    },
  },
  async mounted() {
    await this.loadServiceCategories().catch(() => {
      toastFailure('Can not load service categories')
    })
    this.isLoading = false
  },
  methods: {
    ...mapActions({
      loadServiceCategories: 'serviceCategories/loadServiceCategories',
      deleteServiceCategoryAction: 'serviceCategories/deleteServiceCategory',
    }),
    pictureUrl(picture) {
      return resolvePictureUrl(picture, this.noUnitImage || NO_IMAGE)
    },
    confirmationMessage(message) {
      return this.$bvModal.msgBoxConfirm(message, {
        title: 'Please Confirm',
        okVariant: 'primary',
        okTitle: 'Yes',
        cancelTitle: 'No',
      })
    },
    async deleteServiceCategory(category) {
      const message = `Are you sure you want to delete "${category.name}"? This cannot be undone.`
      if (!await this.confirmationMessage(message)) {
        return
      }

      this.deleteServiceCategoryAction(category.id)
        .then(() => {
          toastSuccess('Service category has been deleted.')
        })
        .catch((error) => {
          const data = error?.response?.data
          const apiMessage = data?.error?.[0] || data?.message
          toastFailure(apiMessage || 'Failed to delete the service category.')
        })
    },
    openServiceCategoryModal(row) {
      this.refreshServiceCategories().finally(() => {
        this.serviceCategory = row
        this.clickDate = new Date()
      })
    },
    refreshServiceCategories() {
      return this.loadServiceCategories().catch(() => {
        toastFailure('Can not load service categories')
      })
    },
  },
}
</script>

<style scoped>
.service-categories-action-col {
  width: 72px !important;
  min-width: 72px !important;
  max-width: 72px !important;
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
}

.service-categories-action {
  display: inline-flex;
  justify-content: center;
  width: auto;
}

.service-categories-action :deep(.dropdown-toggle) {
  width: 40px !important;
  min-width: 40px !important;
  height: 40px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}
</style>
